import clsx from "clsx";
import { FC, useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { AddDeviceFormType } from "@/apps/partner/types";
import { getLanguageTranslationKey } from "@/apps/partner/utils/partnerUtils";
import { Combobox } from "@/components/Interface/FormFields/Combobox/Combobox";
import { DateInput } from "@/components/Interface/FormFields/DateInput/DateInput";
import { FormField } from "@/components/Interface/FormFields/FormField/FormField";
import { FormFieldWithHint } from "@/components/Interface/FormFields/FormField/FormFieldWithHintProps.tsx";
import { ListBox } from "@/components/Interface/FormFields/ListBox/ListBox";
import { PhoneInputField } from "@/components/Interface/FormFields/PhoneInputField/PhoneInputField";
import { RadioButton } from "@/components/Interface/FormFields/RadioButton/RadioButton";
import { type Country, SCHWEIZ } from "@/constants/countries";
import { useGetSortedCountries } from "@/hooks/useGetSortedCountries";
import { SalesPartnerCreateCustomerLanguageEnum } from "@/services/model/salesPartnerCreateCustomerLanguageEnum";
import {
  DACH_HOUSE_NUMBER_REGEX,
  DACH_STREET_NAME_REGEX,
  DEFAULT_TEXT_FIELD_REGEX,
  getPostalCodePattern,
  INTERNATIONAL_NAME_REGEX,
} from "@/utils/constants";
import { validateBirthDate } from "@/utils/customerUtils";

// TODO Refactor the business details logic into a separate component
//  to keep this one's responsibilities small
export const PersonalDetailsFormFields: FC = () => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "Onboarding.sections.personal-details.form",
  });
  const {
    watch,
    register,
    control,
    trigger,
    getValues,
    formState: { errors },
  } = useFormContext<AddDeviceFormType>();

  const sortedCountries = useGetSortedCountries();
  const selectedCountry = watch("customer.countryCodeAlpha2");

  useEffect(() => {
    if (getValues("customer.zip")) {
      trigger("customer.zip");
    }
  }, [trigger, getValues, selectedCountry?.ALPHA2]);

  return (
    <div id="personal-details-form-fields">
      <fieldset
        data-testid="form-of-address-group"
        id="partner-form-of-address"
      >
        <legend className="mb-2 text-body-base font-semibold text-foreground-primary">
          {t(`label.formOfAddress`)}
        </legend>

        <div
          className={clsx(
            "form-of-address",
            errors.customer?.salutationEnum?.type === "required" &&
              "with-errors",
          )}
        >
          <RadioButton
            label={t("formOfAddress.mrs")}
            name="customer.salutationEnum"
            value="2"
            errors={errors}
            register={register}
          />
          <RadioButton
            label={t("formOfAddress.mr")}
            name="customer.salutationEnum"
            value="1"
            errors={errors}
            register={register}
          />
          <RadioButton
            label={t("formOfAddress.other")}
            name="customer.salutationEnum"
            value="0"
            errors={errors}
            register={register}
          />
        </div>
        {errors.customer?.salutationEnum?.type === "required" && (
          <p className="mt-2 text-sm text-foreground-secondary">
            {t("translation:label.validation.required")}
          </p>
        )}
      </fieldset>
      <div className="language" id="partner-contact-language">
        <Controller
          control={control}
          name="customer.languageEnum"
          rules={{
            required: t("translation:label.validation.required"),
          }}
          defaultValue={SalesPartnerCreateCustomerLanguageEnum.DE}
          render={({ field, fieldState: { error } }) => (
            <>
              <div className="label-container">
                <label
                  htmlFor="languageListBox"
                  className={"mb-2 inline-block font-semibold"}
                >
                  {t(`translation:user-account.inbox.contactlanguage` as any)}
                </label>
              </div>
              <ListBox<{ value: string; label: string }>
                className="full-width w-full max-w-none tablet:w-1/2 tablet:pr-2"
                displayValue={(option: { value: string; label: string }) =>
                  t(
                    `translation:user-account.inbox.languages.${getLanguageTranslationKey(option.value)}` as any,
                  )
                }
                availableValues={[
                  {
                    value: SalesPartnerCreateCustomerLanguageEnum.DE,
                    label: "languages.german",
                  },
                  {
                    value: SalesPartnerCreateCustomerLanguageEnum.EN,
                    label: "languages.english",
                  },
                  {
                    value: SalesPartnerCreateCustomerLanguageEnum.FR,
                    label: "languages.french",
                  },
                ]}
                dataTestId="languageListBox"
                placeholder={t("translation:Common.language" as any)}
                by={(a, b) => a?.value === b?.value}
                setSelected={(option) => field.onChange(option?.value)}
                selected={
                  field.value
                    ? {
                        value: field.value,
                        label: String(
                          t(
                            `translation:user-account.inbox.languages.${getLanguageTranslationKey(field.value)}` as any,
                          ),
                        ),
                      }
                    : undefined
                }
              />
              {error && (
                <div className="mt-1 text-sm text-foreground-secondary">
                  {error.message}
                </div>
              )}
            </>
          )}
        />
      </div>

      <div className="names">
        <FormFieldWithHint<AddDeviceFormType>
          id="first-name-input"
          label={t("label.firstName")}
          name="customer.firstName"
          autocomplete="off"
          errors={errors}
          register={register}
          type="text"
          placeholder={t("placeholder.firstName")}
          options={{
            required: t("translation:label.validation.required"),
            pattern: {
              value: INTERNATIONAL_NAME_REGEX,
              message: t(
                "translation:Common.generalForm.error.genericRegexError",
              ),
            },
          }}
        />
        <FormFieldWithHint<AddDeviceFormType>
          id="last-name-input"
          label={t("label.lastName")}
          name="customer.lastName"
          autocomplete="off"
          errors={errors}
          register={register}
          type="text"
          placeholder={t("placeholder.lastName")}
          options={{
            required: t("translation:label.validation.required"),
            pattern: {
              value: INTERNATIONAL_NAME_REGEX,
              message: t(
                "translation:Common.generalForm.error.genericRegexError",
              ),
            },
            setValueAs: (value: string) => value?.trim(),
          }}
        />
      </div>
      <div className="street">
        <FormField<AddDeviceFormType>
          id="street-input"
          label={t("label.street")}
          name="customer.street"
          errors={errors}
          register={register}
          type="text"
          placeholder={t("placeholder.street")}
          options={{
            required: t("translation:label.validation.required"),
            maxLength: 40,
            pattern: {
              value: DACH_STREET_NAME_REGEX,
              message: t(
                "translation:Common.generalForm.error.genericRegexError",
              ),
            },
            setValueAs: (value: string) => value?.trim(),
          }}
        />
        <FormField<AddDeviceFormType>
          id="house-number-input"
          label={t("label.houseNumber")}
          name="customer.houseNumber"
          errors={errors}
          register={register}
          type="text"
          placeholder={t("placeholder.houseNumber")}
          options={{
            required: t("translation:label.validation.required"),
            maxLength: 10,
            pattern: {
              value: DACH_HOUSE_NUMBER_REGEX,
              message: t(
                "translation:Common.generalForm.error.genericRegexError",
              ),
            },
          }}
        />
      </div>
      <div className="addressSupplement">
        <FormField<AddDeviceFormType>
          id="address-supplement-input"
          label={t("label.addressSupplement")}
          name="customer.postalDeliveryNotes"
          errors={errors}
          register={register}
          type="text"
          placeholder={t("placeholder.addressSupplement")}
          options={{
            required: false,
            pattern: {
              value: DEFAULT_TEXT_FIELD_REGEX,
              message: t(
                "translation:Common.generalForm.error.genericRegexError",
              ),
            },
            setValueAs: (value: string) => value?.trim(),
          }}
        />
      </div>
      <div className="zipAndCity">
        <FormField<AddDeviceFormType>
          id="zip-code-input"
          label={t("label.zip")}
          name="customer.zip"
          errors={errors}
          register={register}
          type="text"
          placeholder={t("placeholder.zip")}
          options={{
            required: t("translation:label.validation.required"),
            maxLength: 10,
            pattern: {
              value: getPostalCodePattern(selectedCountry),
              message: t(
                "translation:Common.generalForm.error.genericRegexError",
              ),
            },
          }}
        />
        <FormField<AddDeviceFormType>
          id="city-input"
          label={t("label.city")}
          name="customer.city"
          errors={errors}
          register={register}
          type="text"
          placeholder={t("placeholder.city")}
          options={{
            required: t("translation:label.validation.required"),
            pattern: {
              value: DEFAULT_TEXT_FIELD_REGEX,
              message: t(
                "translation:Common.generalForm.error.genericRegexError",
              ),
            },
            setValueAs: (value: string) => value?.trim(),
          }}
        />
      </div>
      <div className="country">
        <Controller
          control={control}
          name="customer.countryCodeAlpha2"
          rules={{
            required: t("translation:label.validation.required"),
          }}
          defaultValue={SCHWEIZ}
          render={({ field, fieldState: { error } }) => (
            <>
              <Combobox<Country>
                displayValue={(country?: Country) =>
                  t(
                    country
                      ? `translation:countries.${country.ALPHA3}`
                      : "placeholder.country",
                  )
                }
                field={field}
                options={sortedCountries}
                dataTestId="countryCombobox"
                label={t("label.country")}
                placeholder={t("placeholder.country")}
                hasError={!!error}
              />
              {error && (
                <div className="mt-1 text-sm text-foreground-secondary">
                  {error.message}
                </div>
              )}
            </>
          )}
        />
      </div>

      <div className={clsx("phoneAndDateOfBirth")}>
        <PhoneInputField<AddDeviceFormType>
          id="phone-number-input"
          label={t("label.phone")}
          name="customer.contactPhoneNumber"
          control={control}
          errors={errors}
          required={t("translation:label.validation.required")}
          validationMessage={t("translation:label.validation.phone")}
        />

        <DateInput<AddDeviceFormType>
          id="birth-date-input"
          label={t("label.dateOfBirth")}
          name="customer.birthday"
          autocomplete="off"
          errors={errors}
          register={register}
          placeholder={t("placeholder.dateOfBirth")}
          value={watch("customer.birthday")}
          options={{
            required: t("translation:label.validation.required"),
            validate: (value: string) => validateBirthDate({ value, t }),
          }}
        />
      </div>
    </div>
  );
};
